import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Modal, Form, message, Popconfirm, Select, Tag, Card, Row, Col, Pagination, Radio } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, LoginOutlined, UserOutlined, LockOutlined } from '@ant-design/icons';
import { businessScenarioApi, businessDomainApi, userApi } from '../../services/api';
import { formatDateTime } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';
// 引入自定义的员工选择组件
import EmployeeSelector from '../../components/EmployeeSelector';

const { Search, TextArea } = Input;
const { Option } = Select;

function BusinessScenarioList() {
  const navigate = useNavigate();
  const { currentDomain, enterScenario, hasManagePermission, isDomainAdmin, roleLoading } = useWorkspace();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [businessDomains, setBusinessDomains] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState({
    scenarioName: '',
    domainId: undefined,
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  // 添加管理员状态管理
  const [adminUsers, setAdminUsers] = useState([]);
  // 存储每个场景的管理员权限（仅针对项目成员角色）
  const [scenarioAdminMap, setScenarioAdminMap] = useState({});

  // 如果没有选择项目空间，跳转到项目空间列表
  useEffect(() => {
    if (!currentDomain) {
      message.warning('请先选择项目空间');
      navigate('/business-domain');
    }
  }, [currentDomain, navigate]);

  const scenarioCategories = [
    { code: 'AUTO_REVIEW', text: '机审' },
    { code: 'FEATURE_EXTRACTION', text: '要素抽取' },
    { code: 'ROBOT_QA', text: '机器人问答' }
  ];

  useEffect(() => {
    fetchBusinessDomains();
  }, []);

  // 获取数据，依赖分页、搜索参数
  useEffect(() => {
    fetchData();
  }, [pagination.current, pagination.pageSize, searchParams]);

  // 当角色加载完成后，重新检查场景管理员权限
  useEffect(() => {
    // 角色加载完成且不是项目空间管理员时，检查每个场景的管理员权限
    if (!roleLoading && !isDomainAdmin() && dataSource.length > 0) {
      checkScenarioAdminPermissions(dataSource);
    }
  }, [roleLoading]);

  const fetchBusinessDomains = async () => {
    try {
      const response = await businessDomainApi.getList({ pageSize: 1000 });
      setBusinessDomains(response.list || []);
    } catch (error) {
      console.error('获取项目列表失败:', error);
    }
  };

  const fetchData = async () => {
    if (!currentDomain) return;

    setLoading(true);
    try {
      const params = {
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
        scenarioName: searchParams.scenarioName || undefined,
        domainId: currentDomain.id, // 使用当前项目空间的ID
      };

      const response = await businessScenarioApi.getList(params);
      const list = response.list || [];

      setDataSource(list);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0,
      }));

      // 如果当前用户不是项目空间管理员，则需要检查每个场景的管理员权限
      // 注意：这里使用 isDomainAdmin() 而非 hasManagePermission()，
      // 因为 hasManagePermission() 是基于当前进入的场景计算的，而业务场景列表页需要判断每个场景的权限
      if (!isDomainAdmin() && list.length > 0) {
        await checkScenarioAdminPermissions(list);
      }
    } catch (error) {
      console.error('获取业务场景列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 检查当前用户是否为每个场景的管理员
  const checkScenarioAdminPermissions = async (scenarios) => {
    try {
      const results = await Promise.all(
        scenarios.map(async (scenario) => {
          try {
            const isAdmin = await userApi.checkScenarioAdmin(scenario.id);
            return { scenarioId: scenario.id, isAdmin };
          } catch (error) {
            console.error(`检查场景${scenario.id}管理员权限失败:`, error);
            return { scenarioId: scenario.id, isAdmin: false };
          }
        })
      );

      const adminMap = {};
      results.forEach(({ scenarioId, isAdmin }) => {
        adminMap[scenarioId] = isAdmin;
      });
      setScenarioAdminMap(adminMap);
    } catch (error) {
      console.error('检查场景管理员权限失败:', error);
    }
  };

  // 检查是否有特定场景的编辑/删除权限（项目空间管理员 或 是该场景的管理员）
  const hasScenarioPermission = (scenarioId) => {
    return isDomainAdmin() || scenarioAdminMap[scenarioId] === true;
  };

  const handleSearch = (value) => {
    setSearchParams(prev => ({ ...prev, scenarioName: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 进入业务场景
  const handleEnterScenario = (scenario) => {
    enterScenario(scenario);
    navigate('/judge-set');
  };

  const handleAdd = () => {
    setEditingRecord(null);
    setModalVisible(true);
    form.resetFields();
    // 重置管理员状态
    setAdminUsers([]);
    // 如果有当前项目空间，自动设置
    if (currentDomain) {
      form.setFieldsValue({
        domainId: currentDomain.id,
        isEncrypted: 'n', // 是否加密默认为否
      });
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setModalVisible(true);

    // 处理管理员数据格式，适配阿里员工组件
    const adminUsers = record.adminUserInfoList ?
      record.adminUserInfoList.map(user => ({
        workNo: user.workNo,
        displayName: user.nickName || user.name || '',
        label: user.nickName || user.name || '',
        value: user.workNo,
        name: user.name || '',
        nickName: user.nickName || ''
      })) : [];

    // 设置状态
    setAdminUsers(adminUsers);

    // 设置表单字段值
    form.setFieldsValue({
      scenarioName: record.scenarioName,
      scenarioCategory: record.scenarioCategory, // 这里已经是英文code了
      domainId: record.domainId,
      description: record.description,
      adminUsers: adminUsers,
      isEncrypted: record.isEncrypted || 'n', // 是否加密
      encryptionKey: record.encryptionKey || '', // 加密key
    });
  };

  const handleDelete = async (id) => {
    try {
      await businessScenarioApi.delete(id);
      message.success('删除成功');
      fetchData();
    } catch (error) {
      console.error('删除业务场景失败:', error);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      // 处理阿里员工组件的数据格式，提取工号
      const formattedValues = {
        ...values,
        scenarioCategory: values.scenarioCategory, // 保留英文code
        adminUsers: adminUsers ? adminUsers.map(user => user.workNo).join(',') : ''
      };

      // 只有机审和要素抽取场景才提交加密相关字段
      if (values.scenarioCategory === 'AUTO_REVIEW' || values.scenarioCategory === 'FEATURE_EXTRACTION') {
        formattedValues.isEncrypted = values.isEncrypted || 'n';
        // 加密模式时才传递encryptionKey，非加密模式不传递（由后端处理）
        if (values.isEncrypted === 'y') {
          formattedValues.encryptionKey = values.encryptionKey || '';
        } else {
          // 非加密模式：删除encryptionKey字段，让后端自动设置为null
          delete formattedValues.encryptionKey;
        }
      } else {
        // 非机审和要素抽取场景，清除加密相关字段
        delete formattedValues.isEncrypted;
        delete formattedValues.encryptionKey;
      }

      if (editingRecord) {
        await businessScenarioApi.update(editingRecord.id, formattedValues);
        message.success('修改成功');
      } else {
        await businessScenarioApi.create(formattedValues);
        message.success('新增成功');
      }

      setModalVisible(false);
      fetchData();
    } catch (error) {
      console.error('保存业务场景失败:', error);
    }
  };

  const handleTableChange = (paginationConfig) => {
    setPagination(paginationConfig);
  };

  // 处理分页变化
  const handlePaginationChange = (page, pageSize) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize,
    }));
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '业务场景名称',
      dataIndex: 'scenarioName',
      key: 'scenarioName',
      width: 200,
    },
    {
      title: '场景分类',
      dataIndex: 'scenarioCategory',
      key: 'scenarioCategory',
      width: 120,
      render: (category) => {
        const categoryInfo = scenarioCategories.find(c => c.code === category);
        return <Tag color="blue">{categoryInfo?.text || category}</Tag>;
      },
    },
    {
      title: '项目空间',
      dataIndex: 'domainName',
      key: 'domainName',
      width: 120,
      render: (domainName) => (
        <Tag color="green">{domainName}</Tag>
      ),
    },
    {
      title: '说明',
      dataIndex: 'description',
      key: 'description',
      width: 250,
      ellipsis: true,
    },
    {
      title: '管理员',
      dataIndex: 'adminUserInfoList',
      key: 'adminUsers',
      width: 150,
      render: (adminUserInfoList) => {
        const users = adminUserInfoList || [];
        return (
          <div>
            {users.map((user) => {
              const displayName = user.nickName || user.name || '';
              const link = `https://work.alibaba-inc.com/nwpipe/u/${user.workNo}`;
              return (
                <a
                  key={user.workNo}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-700 mr-2"
                >
                  {displayName}
                </a>
              );
            })}
          </div>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      render: (time) => formatDateTime(time),
    },
    {
      title: '创建人',
      dataIndex: 'creatorInfo',
      key: 'creator',
      width: 150,
      render: (creatorInfo) => {
        if (!creatorInfo) return '-';

        const { name, workNo, nickName } = creatorInfo;
        const displayName = name || nickName || '';
        const link = workNo ? `https://work.alibaba-inc.com/nwpipe/u/${workNo}` : '';

        if (link && displayName) {
          return (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700"
            >
              {displayName}{workNo ? `(${workNo})` : ''}
            </a>
          );
        }

        return displayName || '-';
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {hasScenarioPermission(record.id) && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              className="table-action-btn-edit"
            >
              编辑
            </Button>
          )}
          {hasScenarioPermission(record.id) && (
            <Popconfirm
              title="确定要删除这个业务场景吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                size="small"
                icon={<DeleteOutlined />}
                className="table-action-btn-delete"
              >
                删除
              </Button>
            </Popconfirm>
          )}
          <Button
            size="small"
            icon={<LoginOutlined />}
            onClick={() => handleEnterScenario(record)}
            className="table-action-btn-view"
          >
            进入场景
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">业务场景管理</h1>
        <p className="page-description">管理具体的业务场景，每个业务场景对应特定的评测需求</p>
      </div>

      <div className="mb-4 flex justify-between items-center">
        <Search
          placeholder="请输入业务场景名称"
          allowClear
          suffix={<SearchOutlined />}
          size="large"
          className="w-80"
          onSearch={handleSearch}
        />
        {hasManagePermission() && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={handleAdd}
            style={{
              background: '#1890FF',
              borderColor: '#1890FF',
              boxShadow: 'none'
            }}
          >
            新增业务场景
          </Button>
        )}
      </div>

      {/* 卡片展示 */}
      <div style={{ minHeight: '400px' }}>
        <Row gutter={[16, 16]}>
          {loading ? (
            <Col span={24}>
              <div style={{ textAlign: 'center', padding: '50px' }}>
                加载中...
              </div>
            </Col>
          ) : dataSource.length === 0 ? (
            <Col span={24}>
              <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
                暂无数据
              </div>
            </Col>
          ) : (
            dataSource.map(scenario => {
              const isEncrypted = scenario.isEncrypted === 'y';
              
              return (
                <Col xs={24} sm={12} lg={8} xl={6} key={scenario.id} style={{ minWidth: '280px' }}>
                  <Card
                    hoverable
                    style={{
                      height: '240px',
                      display: 'flex',
                      flexDirection: 'column',
                      // 加密场景的特殊样式
                      border: isEncrypted ? '2px solid #FA8C16' : '1px solid #f0f0f0',
                      background: isEncrypted 
                        ? 'linear-gradient(135deg, #FFF7E6 0%, #FFFFFF 100%)' 
                        : '#FFFFFF',
                      boxShadow: isEncrypted 
                        ? '0 2px 8px rgba(250, 140, 22, 0.15)' 
                        : '0 1px 2px rgba(0, 0, 0, 0.03)',
                      position: 'relative',
                      overflow: 'visible'
                    }}
                    styles={{
                      body: {
                        flex: 1,
                        overflow: 'hidden',
                        padding: '16px'
                      },
                      actions: {
                        marginTop: 'auto'
                      }
                    }}
                    actions={[
                      hasScenarioPermission(scenario.id) && (
                        <Button
                          key="edit"
                          type="link"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleEdit(scenario)}
                          style={{ padding: '0 4px', fontSize: '12px' }}
                        >
                          编辑
                        </Button>
                      ),
                      hasScenarioPermission(scenario.id) && (
                        <Popconfirm
                          key="delete"
                          title="确定要删除这个业务场景吗？"
                          onConfirm={() => handleDelete(scenario.id)}
                          okText="确定"
                          cancelText="取消"
                        >
                          <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            style={{ padding: '0 4px', fontSize: '12px' }}
                          >
                            删除
                          </Button>
                        </Popconfirm>
                      ),
                      <Button
                        key="enter"
                        type="link"
                        size="small"
                        icon={<LoginOutlined />}
                        onClick={() => handleEnterScenario(scenario)}
                        style={{ padding: '0 4px', fontSize: '12px' }}
                      >
                        进入
                      </Button>,
                    ].filter(Boolean)}
                  >
                    {/* 加密场景标识 */}
                    {isEncrypted && (
                      <div style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '12px',
                        background: 'linear-gradient(135deg, #FA8C16 0%, #FA541C 100%)',
                        color: '#FFFFFF',
                        padding: '2px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        boxShadow: '0 2px 4px rgba(250, 140, 22, 0.3)',
                        zIndex: 10
                      }}>
                        🔒 加密
                      </div>
                    )}

                    <div style={{ marginBottom: '8px', marginTop: isEncrypted ? '8px' : '0' }}>
                      <h3 style={{
                        margin: 0,
                        fontSize: '16px',
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: isEncrypted ? '#D46B08' : '#000000'
                      }}>
                        {scenario.scenarioName}
                      </h3>
                    </div>

                    <div style={{ marginBottom: '8px' }}>
                      <Tag color={isEncrypted ? 'orange' : 'blue'}>{scenarioCategories.find(c => c.code === scenario.scenarioCategory)?.text || scenario.scenarioCategory}</Tag>
                      <Tag color="green">{scenario.domainName}</Tag>
                      {isEncrypted && (
                        <Tag icon={<LockOutlined />} color="error">
                          加密场景
                        </Tag>
                      )}
                    </div>

                  {scenario.description && (
                    <div style={{
                      marginBottom: '6px',
                      fontSize: '12px',
                      color: '#666',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      minHeight: '32px'
                    }}>
                      {scenario.description}
                    </div>
                  )}

                  <div style={{ marginBottom: '6px', fontSize: '12px', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <UserOutlined style={{ marginRight: '4px' }} />
                    <span>管理员：</span>
                    {(scenario.adminUserInfoList || []).slice(0, 4).map((user, index) => (
                      <span key={user.workNo}>
                        {index > 0 && '、'}
                        {user.nickName || user.name}
                      </span>
                    ))}
                    {(scenario.adminUserInfoList || []).length > 4 && (
                      <span>...</span>
                    )}
                  </div>

                  <div style={{ fontSize: '12px', color: '#999' }}>
                    创建时间：{formatDateTime(scenario.gmtCreate)}
                  </div>
                </Card>
              </Col>
              );
            })
          )}
        </Row>
      </div>

      {/* 分页 */}
      <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          showSizeChanger
          showQuickJumper
          showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条/总共 ${total} 条`}
          onChange={handlePaginationChange}
          pageSizeOptions={['12', '24', '36', '48']}
        />
      </div>

      <Modal
        title={editingRecord ? '编辑业务场景' : '新增业务场景'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={700}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          className="mt-4"
        >
          <Form.Item
            name="scenarioName"
            label="业务场景名称"
            rules={[
              { required: true, message: '请输入业务场景名称' },
              { max: 100, message: '业务场景名称不能超过100个字符' },
            ]}
          >
            <Input placeholder="请输入业务场景名称" />
          </Form.Item>

          <Form.Item
            name="scenarioCategory"
            label="场景分类"
            rules={[
              { required: true, message: '请选择场景分类' },
            ]}
          >
            <Select placeholder="请选择场景分类">
              {scenarioCategories.map(category => (
                <Option key={category.code} value={category.code}>
                  {category.text}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* 机审和要素抽取场景特有字段：是否加密 */}
          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.scenarioCategory !== currentValues.scenarioCategory}>
            {({ getFieldValue }) => {
              const scenarioCategory = getFieldValue('scenarioCategory');
              if (scenarioCategory !== 'AUTO_REVIEW' && scenarioCategory !== 'FEATURE_EXTRACTION') {
                return null;
              }
              return (
                <>
                  <Form.Item
                    name="isEncrypted"
                    label="是否加密"
                    initialValue="n"
                    extra={
                      editingRecord
                        ? '加密模式创建后不可更改'
                        : '注意：加密模式一旦选择后将不可修改，请谨慎选择'
                    }
                  >
                    <Radio.Group disabled={!!editingRecord}>
                      <Radio value="y">是</Radio>
                      <Radio value="n">否</Radio>
                    </Radio.Group>
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>

          <Form.Item
            name="domainId"
            label="项目空间"
            rules={[
              { required: true, message: '请选择项目空间' },
            ]}
          >
            <Select placeholder="请选择项目空间" disabled={!!currentDomain}>
              {businessDomains.map(domain => (
                <Option key={domain.id} value={domain.id}>
                  {domain.domainName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="说明"
          >
            <TextArea
              rows={4}
              placeholder="请输入业务场景说明"
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="adminUsers"
            label="管理员"
            rules={[
              { required: true, message: '请输入管理员' },
            ]}
          >
            <EmployeeSelector
              name="adminUsers"
              required={true}
              multiple={true}
              value={adminUsers}
              onChange={(value) => {
                setAdminUsers(value || []);
                // 同步更新表单字段的值
                form.setFieldsValue({ adminUsers: value || [] });
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default BusinessScenarioList;